import React, { useState, useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { useTranslation } from "react-i18next";
import { IoIosArrowRoundForward, IoIosArrowRoundBack } from "react-icons/io";
import { FaCode, FaLaptopCode, FaDatabase, FaServer } from "react-icons/fa";
import GlowingBorder from "../animations/GlowingBorder";
import TypewriterPlaceholder from "../animations/TypewriterPlaceholder";
import CodeBlockAnimation from "../animations/CodeBlockAnimation";

const SoftwareDevContactForm = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [activePlaceholder, setActivePlaceholder] = useState(null);
  const [showTypingAnimation, setShowTypingAnimation] = useState(true);
  
  // Animation controls
  const formControls = useAnimation();
  const titleControls = useAnimation();
  
  // Start animations when component mounts
  useEffect(() => {
    const sequence = async () => {
      await titleControls.start({ opacity: 1, y: 0 });
      await formControls.start({ opacity: 1, y: 0 });
    };
    
    sequence();
    
    // Hide typing animation after 10 seconds
    const timer = setTimeout(() => {
      setShowTypingAnimation(false);
    }, 10000);
    
    return () => clearTimeout(timer);
  }, [titleControls, formControls]);
  
  // Placeholder suggestions for the typewriter effect
  const namePlaceholders = [
    t("your_name"),
    t("John Doe"),
    t("Enter your full name"),
  ];
  
  const emailPlaceholders = [
    t("your_email"),
    "example@company.com",
    t("Enter your email address"),
  ];
  
  const messagePlaceholders = [
    t("describe_your_project"),
    t("I need a web application for..."),
    t("We are looking for a mobile app that..."),
    t("Tell us about your requirements"),
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = t("software_name_required");
    if (!formData.email) {
      newErrors.email = t("software_email_required");
    } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) {
      newErrors.email = t("software_invalid_email");
    }
    if (!formData.message) newErrors.message = t("software_message_required");
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    
    if (Object.keys(validationErrors).length === 0) {
      setIsSubmitting(true);
      
      // Simulate API call
      setTimeout(() => {
        console.log("Form submitted:", formData);
        setFormData({ name: "", email: "", message: "" });
        setIsSubmitting(false);
        setSubmitSuccess(true);
        
        // Reset success message after 5 seconds
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 5000);
      }, 1000);
    } else {
      setErrors(validationErrors);
    }
  };

  return (
    <div id="software-contact" className="bg-gradient-to-b from-gray-900 to-black py-20">
      <div className="container mx-auto px-4 md:px-8">
        <GlowingBorder className="mb-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden relative"
          >
            {/* Background animated particles */}
            <div className="absolute inset-0 overflow-hidden">
              {[...Array(20)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-1 h-1 bg-primary rounded-full"
                  style={{
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    opacity: 0.2,
                  }}
                  animate={{
                    y: [0, Math.random() > 0.5 ? 100 : -100],
                    x: [0, Math.random() > 0.5 ? 50 : -50],
                    opacity: [0.2, 0.5, 0],
                  }}
                  transition={{
                    duration: 5 + Math.random() * 5,
                    repeat: Infinity,
                    repeatType: "reverse",
                  }}
                />
              ))}
            </div>
          <div className="md:flex">
            {/* Left side - Form */}
            <div className={`md:w-2/3 p-8 md:p-12 ${isRTL ? 'text-right' : ''} relative z-10`}>
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={titleControls}
                transition={{ duration: 0.7 }}
              >
                <motion.h2 
                  className="text-3xl md:text-4xl font-bold text-white mb-2"
                  animate={{ 
                    textShadow: [
                      '0 0 5px rgba(255,204,0,0)', 
                      '0 0 10px rgba(255,204,0,0.3)', 
                      '0 0 5px rgba(255,204,0,0)'
                    ]
                  }}
                  transition={{ 
                    repeat: Infinity, 
                    duration: 3,
                    ease: "easeInOut" 
                  }}
                >
                  {t("Contact Our")} <span className="text-primary">{t("Development Team")}</span>
                </motion.h2>
                <motion.p 
                  className="text-gray-400 mb-8"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                >
                  {t("Fill out this form to discuss your software development needs with our expert team.")}
                </motion.p>
              </motion.div>
              
              <motion.form 
                onSubmit={handleSubmit}
                initial={{ opacity: 0, y: 20 }}
                animate={formControls}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <motion.div 
                  className="mb-6"
                  whileHover={{ scale: 1.01 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <motion.div
                    whileFocus={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <input
                      type="text"
                      name="name"
                      placeholder={showTypingAnimation && !formData.name ? 
                        <TypewriterPlaceholder 
                          placeholders={namePlaceholders} 
                          currentPlaceholder={activePlaceholder === 'name' ? t("your_name") : null}
                        /> : 
                        t("your_name")
                      }
                      value={formData.name}
                      onChange={handleChange}
                      onFocus={() => setActivePlaceholder('name')}
                      className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border ${
                        errors.name ? "border-red-500" : "border-gray-700"
                      } focus:outline-none focus:border-primary transition-colors focus:shadow-[0_0_10px_rgba(255,204,0,0.3)]`}
                    />
                  </motion.div>
                  {errors.name && (
                    <motion.span 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-500 text-sm mt-1 block"
                    >
                      {errors.name}
                    </motion.span>
                  )}
                </motion.div>
                
                <motion.div 
                  className="mb-6"
                  whileHover={{ scale: 1.01 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <motion.div
                    whileFocus={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <input
                      type="email"
                      name="email"
                      placeholder={showTypingAnimation && !formData.email ? 
                        <TypewriterPlaceholder 
                          placeholders={emailPlaceholders} 
                          currentPlaceholder={activePlaceholder === 'email' ? t("your_email") : null}
                        /> : 
                        t("your_email")
                      }
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={() => setActivePlaceholder('email')}
                      className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border ${
                        errors.email ? "border-red-500" : "border-gray-700"
                      } focus:outline-none focus:border-primary transition-colors focus:shadow-[0_0_10px_rgba(255,204,0,0.3)]`}
                    />
                  </motion.div>
                  {errors.email && (
                    <motion.span 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-500 text-sm mt-1 block"
                    >
                      {errors.email}
                    </motion.span>
                  )}
                </motion.div>
                
                <motion.div 
                  className="mb-6"
                  whileHover={{ scale: 1.01 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <motion.div
                    whileFocus={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <textarea
                      name="message"
                      placeholder={showTypingAnimation && !formData.message ? 
                        <TypewriterPlaceholder 
                          placeholders={messagePlaceholders} 
                          currentPlaceholder={activePlaceholder === 'message' ? t("describe_your_project") : null}
                        /> : 
                        t("describe_your_project")
                      }
                      value={formData.message}
                      onChange={handleChange}
                      onFocus={() => setActivePlaceholder('message')}
                      rows="4"
                      className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border ${
                        errors.message ? "border-red-500" : "border-gray-700"
                      } focus:outline-none focus:border-primary transition-colors focus:shadow-[0_0_10px_rgba(255,204,0,0.3)]`}
                    ></textarea>
                  </motion.div>
                  {errors.message && (
                    <motion.span 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-500 text-sm mt-1 block"
                    >
                      {errors.message}
                    </motion.span>
                  )}
                </motion.div>
                
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className={`inline-flex items-center bg-primary text-black px-6 py-3 rounded-lg font-bold hover:bg-yellow-400 transition-colors ${
                    isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isSubmitting ? t("sending...") : t("send_message")}
                  <span className={`${isRTL ? 'mr-2' : 'ml-2'} bg-black/20 rounded-full p-1`}>
                    {isRTL ? (
                      <IoIosArrowRoundBack className="w-5 h-5" />
                    ) : (
                      <IoIosArrowRoundForward className="w-5 h-5" />
                    )}
                  </span>
                </motion.button>
                
                {submitSuccess && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 text-green-400 p-4 border border-green-500 rounded-lg bg-green-500/10"
                  >
                    <motion.div 
                      animate={{ 
                        scale: [1, 1.05, 1],
                      }}
                      transition={{ 
                        duration: 2,
                        repeat: 3,
                        ease: "easeInOut" 
                      }}
                    >
                      {t("Thank you! Your message has been sent successfully.")}
                    </motion.div>
                  </motion.div>
                )}
              </motion.form>
            </div>
            
            {/* Right side - Decorative */}
            <div className="hidden md:block md:w-1/3 bg-gray-800 relative overflow-hidden">
              {/* Code block animation */}
              <CodeBlockAnimation />
              
              {/* Floating tech icons */}
              <div className="absolute inset-0 z-10 opacity-70">
                <motion.div
                  className="absolute text-blue-400 text-5xl"
                  style={{ top: '15%', left: '20%' }}
                  animate={{
                    y: [0, -15, 0],
                    rotate: [0, 5, 0, -5, 0],
                    opacity: [0.4, 0.7, 0.4]
                  }}
                  transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <FaCode />
                </motion.div>
                
                <motion.div
                  className="absolute text-blue-300 text-4xl"
                  style={{ top: '40%', left: '60%' }}
                  animate={{
                    y: [0, 15, 0],
                    rotate: [0, -5, 0, 5, 0],
                    opacity: [0.3, 0.6, 0.3]
                  }}
                  transition={{
                    duration: 7,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1
                  }}
                >
                  <FaLaptopCode />
                </motion.div>
                
                <motion.div
                  className="absolute text-blue-500 text-3xl"
                  style={{ top: '70%', left: '30%' }}
                  animate={{
                    y: [0, 10, 0],
                    x: [0, 5, 0, -5, 0],
                    opacity: [0.2, 0.5, 0.2]
                  }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 2
                  }}
                >
                  <FaDatabase />
                </motion.div>
                
                <motion.div
                  className="absolute text-primary text-4xl"
                  style={{ top: '85%', left: '70%' }}
                  animate={{
                    y: [0, -10, 0],
                    rotate: [0, 10, 0, -10, 0],
                    opacity: [0.3, 0.7, 0.3]
                  }}
                  transition={{
                    duration: 9,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 3
                  }}
                >
                  <FaServer />
                </motion.div>
              </div>
            </div>
          </div>
          </motion.div>
        </GlowingBorder>
      </div>
    </div>
  );
};

export default SoftwareDevContactForm;
